// Service Worker Types

/**
 * Message types for service worker communication
 */
export const SW_MESSAGE_TYPES = {
  REGISTER_APP: "REGISTER_APP",
  SHOULD_UPDATE_APP: "SHOULD_UPDATE_APP",
  SET_DEBUG_MODE: "SET_DEBUG_MODE",
} as const;

/**
 * Type union of all service worker message types
 */
export type SWMessageType =
  (typeof SW_MESSAGE_TYPES)[keyof typeof SW_MESSAGE_TYPES];

export interface AppFiles {
  [filePath: string]: ArrayBuffer; // filePath -> file content as binary
}

/**
 * App update check information (without files)
 */
export interface AppUpdateCheck {
  baseUrl: string;
  project: string;
  appId: string;
  lastUpdatedTime: number; // Unix timestamp (ms)
}

/**
 * App registration information for caching
 */
export interface AppRegistration {
  baseUrl: string;
  project: string;
  appId: string;
  lastUpdatedTime: number; // Unix timestamp (ms)
  files: AppFiles;
}

export interface CacheEntry {
  files: AppFiles;
  lastUpdatedTime: number; // Unix timestamp (ms)
}

export interface RegisterAppMessage {
  type: typeof SW_MESSAGE_TYPES.REGISTER_APP;
  payload: {
    baseUrl: string;
    project: string;
    appId: string;
    lastUpdatedTime: number; // Unix timestamp (ms)
    files: AppFiles;
  };
}

export interface RegisterAppResponse {
  success: boolean;
  message: string;
  cacheKey: string | undefined;
}

export interface ShouldUpdateAppMessage {
  type: typeof SW_MESSAGE_TYPES.SHOULD_UPDATE_APP;
  payload: {
    baseUrl: string;
    project: string;
    appId: string;
    lastUpdatedTime: number; // Unix timestamp (ms)
  };
}

export interface ShouldUpdateAppResponse {
  shouldUpdate: boolean;
  message: string;
  cacheKey: string | undefined;
  currentLastUpdated?: number; // Unix timestamp (ms)
}

export interface SetDebugModeMessage {
  type: typeof SW_MESSAGE_TYPES.SET_DEBUG_MODE;
  payload: {
    enabled: boolean;
  };
}

/**
 * Union type for all incoming messages to service worker
 */
export type SWIncomingMessage =
  | RegisterAppMessage
  | ShouldUpdateAppMessage
  | SetDebugModeMessage;

/**
 * Union type for all outgoing messages from service worker
 */
export type SWOutgoingMessage = RegisterAppResponse | ShouldUpdateAppResponse;
